import type { captureException, setUser, getCurrentScope, getFeedback } from '@sentry/nextjs';

import { displayModeConfigAtom } from '@repo/shared/state';
import { JOTAI_STORE } from '@repo/shared/state/core/jotai';
import { getIsolatedSentryScope } from '@repo/shared/utils/sentry-isolated-scope';

// `ignoreErrors` is tested against the exception value, and `getMessageForObject` only uses a thrown
// plain object's message verbatim when it has no `name`. With one - which `parseResponseToJson` sets,
// as do the `{} as Error` shapes the error surfaces build - the value becomes
// `'Error' captured as exception with message '...'`, and every `^`-anchored entry stops matching.
const toCapturableException = (exception: unknown): unknown => {
	if (exception instanceof Error || typeof exception !== 'object' || exception === null) {
		return exception;
	}
	const { name, message, stack, cause } = exception as Partial<Error>;
	if (typeof message !== 'string' || message?.length === 0) {
		return exception;
	}
	const capturableException = new Error(message, { cause: cause ?? exception });
	if (typeof name === 'string' && name?.length > 0) {
		capturableException.name = name;
	}
	// Deliberately not wiped when the thrown value had none: `core.ts` appends a unique request id to
	// the message, so a frameless event would group on type and value and file one issue per request.
	if (typeof stack === 'string' && stack?.length > 0) {
		capturableException.stack = stack;
	}
	return capturableException;
};

export const sentryCaptureException = async (exception: unknown, captureContext?: Parameters<typeof captureException>[1]): Promise<string | null> => {
	try {
		const capturableException = toCapturableException(exception);
		// `eventFromError` attaches no `extra`, unlike the plain-object path a rebuild replaces, and a
		// plain-object `cause` is never walked either, so `status` and `requestId` would be dropped.
		const capturableCaptureContext = (capturableException === exception) ? captureContext : ({ ...((typeof captureContext === 'object' && captureContext !== null) ? captureContext : {}), extra: { ...((captureContext as { extra?: Record<string, unknown>; })?.extra), __serialized__: exception } } as Parameters<typeof captureException>[1]);
		const isolatedScope = getIsolatedSentryScope();
		if (isolatedScope) {
			// `Scope#captureException`'s second parameter is an `EventHint`, not the `CaptureContext`
			// the global `captureException` takes, so passing ours through would silently discard the
			// `tags` several call sites rely on. Apply it to a throwaway clone instead, which also
			// keeps these tags off every later event — the same semantics as the global helper.
			const captureScope = (typeof capturableCaptureContext === 'object' && capturableCaptureContext !== null) ? isolatedScope?.clone?.()?.update?.(capturableCaptureContext) : isolatedScope;
			return captureScope?.captureException?.(capturableException);
		}
		const Sentry = await import('@sentry/nextjs');
		return Sentry.captureException(capturableException, capturableCaptureContext);
	} catch {
		return null;
	}
};

// A whitelabel page is our own Next.js document, so its global Sentry client is ours; the embed SDK must never fall back.
export const canUseGlobalSentryBreadcrumbs = (): boolean => {
	const displayModeConfig = JOTAI_STORE.get(displayModeConfigAtom);
	return displayModeConfig?.isWhitelabelDomainMode === true && !displayModeConfig?.isEmbeddedWidgetMode;
};

export const sentryAddBreadcrumb = async (breadcrumb: { category: string; type?: string; level?: 'info' | 'warning' | 'error'; message?: string; data?: Record<string, unknown>; }, { allowGlobalFallback = false }: { allowGlobalFallback?: boolean; } = {}): Promise<void> => {
	try {
		// The global fallback is opt-in, unlike the helpers around it. The global `addBreadcrumb`
		// writes to the shared isolation scope, whose client on a customer's page is *their* Sentry.
		// So before the widget's async init lands its scope, a fallback would file our breadcrumbs
		// against their project. Only a caller that knows it runs in our own document may opt in.
		const isolatedScope = getIsolatedSentryScope();
		if (isolatedScope) {
			isolatedScope?.addBreadcrumb?.({ timestamp: Date.now() / 1000, ...breadcrumb });
			return;
		}
		if (allowGlobalFallback) {
			const Sentry = await import('@sentry/nextjs');
			Sentry.addBreadcrumb({ timestamp: Date.now() / 1000, ...breadcrumb });
		}
	} catch {
		// Do nothing
	}
};

export const sentrySetUser = async (user: Parameters<typeof setUser>[0]): Promise<void> => {
	try {
		const isolatedScope = getIsolatedSentryScope();
		if (isolatedScope) {
			isolatedScope?.setUser?.(user);
			return;
		}
		const Sentry = await import('@sentry/nextjs');
		Sentry.setUser(user);
	} catch {
		// Do nothing
	}
};

export const sentrySetContext = async (name: string, context: Record<string, unknown> | null): Promise<void> => {
	try {
		const isolatedScope = getIsolatedSentryScope();
		if (isolatedScope) {
			isolatedScope?.setContext?.(name, context);
			return;
		}
		const Sentry = await import('@sentry/nextjs');
		Sentry.setContext(name, context);
	} catch {
		// Do nothing
	}
};

export const sentrySetTags = async (tags: Record<string, string | number | boolean | null | undefined>): Promise<void> => {
	try {
		const isolatedScope = getIsolatedSentryScope();
		if (isolatedScope) {
			isolatedScope?.setTags?.(tags);
			return;
		}
		const Sentry = await import('@sentry/nextjs');
		Sentry.setTags(tags);
	} catch {
		// Do nothing
	}
};

export const sentryGetCurrentScope = async (): Promise<ReturnType<typeof getCurrentScope> | undefined> => {
	try {
		const isolatedScope = getIsolatedSentryScope();
		if (isolatedScope) {
			return isolatedScope;
		}
		const Sentry = await import('@sentry/nextjs');
		return Sentry.getCurrentScope();
	} catch {
		return undefined;
	}
};

// Deliberately not routed through the isolated scope: the feedback widget is a global-SDK integration
// the widget does not load, and it is only ever used by the dashboards' fullscreen error surfaces.
export const sentryGetFeedback = async (): Promise<ReturnType<typeof getFeedback> | undefined> => {
	try {
		const Sentry = await import('@sentry/nextjs');
		return Sentry.getFeedback();
	} catch {
		return undefined;
	}
};
